<template>
	<AccessibleSection
		name="tasks"
		:tabOrder="tabOrder"
		:wait="500"
		class="content-wrapper-no-scroll"
		ref="taskscontent"
		:class="{
			'announcements-banner-tasks-open':
				store.showAnnouncementsBanner || store.askForNotificationPermission,
			'announcements-banner-tasks-close':
				!store.showAnnouncementsBanner || !store.askForNotificationPermission,
		}"
	>
		<div class="task-content">
			<div class="pronto-link-button-wrapper">
				<div class="lg-heading" v-t="'tasks_drawer_title'" />
				<Button link id="taskLink" @click="newTask">
					{{ `+ ${$t('tasks_newtask')}` }}
				</Button>
			</div>

			<div class="task-divider"></div>
			<div class="task-list-wrapper">
				<div
					v-show="!showCompleted"
					class="toggle-completed-tasks pointer link-button"
					@click="loadCompletedTaskList(true)"
					role="button"
				>
					<img src="/images/common/open-tasks-icon.png" />
					<span>{{ $t('tasks_list_helper_showcompleted') }}</span>
				</div>

				<div
					v-show="showCompleted"
					class="toggle-completed-tasks pointer link-button"
					@click="showCompleted = false"
				>
					<img src="/images/common/completed-tasks-icon.png" />
					<span class="hide-completed">{{
						$t('tasks_list_helper_hidecompleted')
					}}</span>
				</div>

				<div v-if="isLoading" id="taskListLoader">
					<span>
						<img src="/images/pronto/3-dot-loader.gif" />
						<p>
							{{ $t('common_loader_loading_title') }}
						</p>
					</span>
				</div>

				<div
					id="noMoreTasks"
					v-if="noOpenTasks && !showCompleted && !isLoading"
				>
					{{ $t('tasks_list_empty_mytasks') }}
				</div>

				<div
					id="noMoreTasks"
					v-if="noCompletedTasks && showCompleted && !isLoading"
				>
					{{ $t('tasks_list_empty_completed') }}
				</div>

				<div v-if="!showCompleted && !isLoading && !noOpenTasks">
					<div
						v-for="(tasksOnDate, dateStr, index) in openTasksByDate"
						:key="dateStr"
					>
						<div v-if="showDate(dateStr, index)" class="task-list-header">
							{{ taskListDate(dateStr) }}
						</div>
						<div v-if="showDate(dateStr, index)" class="task-list-sub-header">
							{{ taskListDOW(dateStr) }}
						</div>
						<ul class="task-list">
							<li
								v-for="task in tasksOnDate"
								@click="editTask(task)"
								:key="task.id"
							>
								<div
									class="task-list-checkbox icon-button"
									id="taskCompleteCheckbox"
									@click.stop="completeTask(task)"
								></div>
								<div class="task-li-content">
									<label class="task-li-main" data-private>{{
										task.title
									}}</label>
									<label class="task-li-sub">{{
										$t('tasks_assignedto', [displayAssignee(task)])
									}}</label>
								</div>
								<div class="task-list-reminder">
									{{ displayOverDueDate(task.due) }}
								</div>
								<img
									class="task-list-arrow"
									src="/images/common/task-list-arrow.png"
								/>
							</li>
						</ul>
					</div>
				</div>

				<div v-if="showCompleted && !isLoading && !noCompletedTasks">
					<div
						v-for="(tasksOnDate, dateStr) in completedTasksByDate"
						:key="dateStr"
					>
						<div class="task-list-header">{{ completedDate(dateStr) }}</div>
						<div class="task-list-sub-header">
							{{ completedListDOW(dateStr) }}
						</div>
						<ul class="task-list">
							<li
								class="task-completed"
								v-for="task in tasksOnDate"
								:key="task.id"
								@click="editTask(task)"
							>
								<div
									class="task-list-checkbox icon-button"
									id="taskUnCompleteCheckbox"
									@click.stop="unCompleteTask(task)"
								></div>
								<div class="task-li-content">
									<label class="task-li-main" data-private>{{
										task.title
									}}</label>
									<label class="task-li-sub">{{
										$t('tasks_assignedto', [displayAssignee(task)])
									}}</label>
								</div>
								<div class="task-list-reminder"></div>
								<img
									class="task-list-arrow"
									src="/images/common/task-list-arrow-completed.png"
								/>
							</li>
						</ul>
					</div>
				</div>
			</div>
		</div>

		<TaskDetail
			v-if="showTaskDetail"
			@close="showTaskDetail = false"
			@taskdeleted="deleteTask"
			@taskcomplete="completeTask"
			@taskuncomplete="unCompleteTask"
			:taskdata="taskData"
			:loading="isLoading"
		/>

		<FailBanner v-if="showFail" @close="showFail = false">
			<template #alerttext>{{ alertText }}</template>
		</FailBanner>
	</AccessibleSection>
</template>

<script>
import moment from 'moment'
import store, { getChat, getGroupChats } from '../store'
import { post } from '../helpers/api'
import TaskDetail from './TaskDetail'
import FailBanner from './FailBanner'
import { dayOfWeek } from '@/helpers/utils'
import { Button } from '@/components/common'
import { AccessibleSection } from '@/components/common/Accessible'

export const sortByDue = (array) => {
	return array.slice().sort(function (a, b) {
		if (a.due === b.due) {
			return a.id - b.id
		}
		return a.due > b.due ? 1 : a.due < b.due ? -1 : 0
	}, {})
}

const sortByCompleted = (array) => {
	return array.slice().sort(function (a, b) {
		if (b.completed === a.completed) {
			return b.id - a.id
		}
		return b.completed > a.completed ? 1 : b.completed < a.completed ? -1 : 0
	}, {})
}

export function groupBy(array, prop) {
	return array.reduce(function (groups, item) {
		if (prop === 'due') {
			var val = moment(item[prop]).format('YYYY-MM-DD')
		} else {
			var val = moment.utc(item[prop]).local().format('YYYY-MM-DD')
		}

		groups[val] = groups[val] || []
		groups[val].push(item)
		return groups
	}, {})
}

export default {
	components: { TaskDetail, FailBanner, AccessibleSection, Button },
	data() {
		return {
			store,
			disabled: {
				to: '',
			},
			showCalendar: false,
			showReminder: false,
			showAssignTo: false,
			showCompleted: false,
			showNotes: false,
			personActive: true,
			showTaskDetail: false,
			isLoading: false,
			showFail: false,
			alertText: false,
			userList: [],
			groupList: [],
			personMatches: [],
			groupMatches: [],
			search: '',
			searching: false,
			hour: '',
			hours: [],
			minute: '',
			minutes: [],
			time: '',
			times: [],
			dueDate: '',
			reminderDate: '',
			title: '',
			notes: '',
			isAssigned: false,
			assignedUser: '',
			assignedGroup: '',
			assignedTitle: this.$t('tasks_assignee_title'),
			taskData: {},
		}
	},

	watch: {
		search(val) {
			if (this.search.length > 0) {
				this.searching = true
				if (this.personActive) {
					this.personMatches = this.userList.filter(function (match) {
						return match.name.toLowerCase().indexOf(val.toLowerCase()) > -1
					})
				} else {
					this.groupMatches = this.groupList.filter(function (match) {
						return match.title.toLowerCase().indexOf(val.toLowerCase()) > -1
					})
				}
			} else {
				this.searching = false
			}
		},
	},

	mounted() {
		this.loadTaskList(true)

		// Check for new query param (from wrapper popout)
		const { new: createNew } = this.$route.query
		if (createNew) {
			this.newTask()
			this.$router.replace({ query: {} })
		}

		let today = new Date()
		this.dueDate = today

		let yesterday = new Date(today)
		yesterday.setDate(today.getDate() - 1)

		this.disabled.to = yesterday

		this.minutes = Array.from(Array(60).keys())
			.map((x) => (x < 10 ? `0${x}` : x + ''))
			.map((x) => ({ text: x, value: x }))

		this.hours = Array.from(Array(12).keys())
			.map((x) => x + 1)
			.map((x) => (x < 10 ? `0${x}` : x + ''))
			.map((x) => ({ text: x, value: x }))

		this.times = [
			{
				text: 'AM',
				value: 'AM',
			},

			{
				text: 'PM',
				value: 'PM',
			},
		]

		this.getGroups()
	},

	computed: {
		openTasksByDate() {
			const open_tasks = sortByDue(this.store.tasks.open)
			return groupBy(open_tasks, 'due')
		},

		completedTasksByDate() {
			const completed_tasks = sortByCompleted(this.store.tasks.completed)
			return groupBy(completed_tasks, 'completed')
		},

		noOpenTasks() {
			if (this.store.tasks.open.length == 0) {
				return true
			} else {
				return false
			}
		},

		noCompletedTasks() {
			if (this.store.tasks.completed.length == 0) {
				return true
			} else {
				return false
			}
		},

		tabOrder() {
			return [
				{ css: '#taskLink', clickOnEnter: true },
				{ css: '.toggle-completed-tasks', clickOnEnter: true },
				{ all: '.task-list > li', clickOnEnter: true },
			]
		},
	},

	methods: {
		displayOverDueDate(date) {
			// This date is a "floating date" which means it should be
			// interpretted as being in the user's local timezone
			const today = moment().startOf('day')
			let dateDue = moment(date).local()

			if (today.isAfter(dateDue)) {
				return dateDue.format('ll')
			} else {
				return ''
			}
		},

		dayOfWeek(date) {
			return dayOfWeek(date)
		},

		showDate(date, index) {
			return index === 0 || moment(date).isAfter(moment(), 'day')
		},

		taskListDate(date) {
			// This date is a "floating date" which means it should be
			// interpretted as being in the user's local timezone
			if (moment(date).isSameOrBefore(moment(), 'day')) {
				return this.$t('common_today')
			}
			return moment(date).format('ll').toUpperCase()
		},

		taskListDOW(date) {
			// This date is a "floating date" which means it should be
			// interpretted as being in the user's local timezone
			const completed = moment(date)
			if (completed.isAfter(moment(), 'day')) {
				return completed.format('dddd').toUpperCase()
			} else {
				return ''
			}
		},

		completedDate(date) {
			// Completed date comes from the api in UTC but in this case
			// this date has already been converted to local time
			const DOW = this.dayOfWeek(date)
			return DOW === 'TODAY'
				? this.$t('common_today')
				: moment(date).format('ll').toUpperCase()
		},

		completedListDOW(date) {
			// Completed date comes from the api in UTC but in this case
			// this date has already been converted to local time
			const completed = moment(date)
			if (completed.isBefore(moment(), 'day')) {
				return completed.format('dddd').toUpperCase()
			}
			return ''
		},

		getOrgUsers() {
			let org_id = this.store.auth.user.organizations[0].id
			post('/api/v1/organization.users', { organization_id: org_id })
				.then(({ data }) => {
					var user_array = []
					if (data.users.length > 0) {
						data.users.forEach(function (user, index) {
							var userObj = {
								id: user.id,
								name: user.firstname + ' ' + user.lastname,
							}

							user_array.push(userObj)
						})

						this.userList = this.store.sortedUserList(user_array)
					}
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(error)
				})
		},

		getGroups() {
			this.groupList = this.store.sortedGroupList(
				getGroupChats().map((b) => ({ id: b.id, title: b.title }))
			)
		},

		getAssignee(task) {
			if (task.bubble_id && !task.assigneeuser_id) {
				return getChat(task.bubble_id).displayname
			} else {
				return task.assigneeuser.firstname + ' ' + task.assigneeuser.lastname
			}
		},

		displayAssignee(task) {
			if (task.bubble_id && !task.assigneeuser_id) {
				return getChat(task.bubble_id).displayname
			} else {
				return task.assigneeuser.firstname + ' ' + task.assigneeuser.lastname
			}
		},

		calcReminder() {
			if (!this.reminderDate) {
				let today = moment().local().format('YYYY-MM-DD HH:mm:ss')

				this.reminderDate = this.dueDate
				this.minute = '00'

				if (moment(today).isSame(this.dueDate, 'd')) {
					var m = moment(today)
					var nextHour =
						m.minute() || m.second()
							? m.add(1, 'hour').startOf('hour')
							: m.startOf('hour')

					if (m.hour() < 9) {
						this.hour = '09'
						this.time = 'AM'
					} else if (moment(today).add(5, 'minutes').isBefore(nextHour)) {
						this.reminderDate = this.dueDate
						this.hour = moment(nextHour).hour()
						this.time = moment(nextHour).format('A')
					} else {
						this.reminderDate = this.dueDate
						this.hour = moment(nextHour).add(1, 'hour').hour()
						this.time = moment(nextHour).format('A')
					}
				} else {
					this.reminderDate = this.dueDate
					this.hour = '09'
					this.time = 'AM'
				}
			}
		},

		loadTaskList(loading, oldest) {
			this.isLoading = loading

			let post_data = {
				organization_id: this.store.auth.user.organizations[0].id,
				completed: false,
			}

			if (oldest) {
				post_data.latest = oldest
			}

			post('/api/v1/task.list', post_data)
				.then(({ data }) => {
					if (oldest) {
						this.store.tasks.open = this.store.tasks.open.concat(data.tasks)
					} else {
						this.store.tasks.open = data.tasks
					}

					this.$nextTick(function () {
						if (data.hasmore) {
							let oldest_task = data.tasks.reduce(function (prev, curr) {
								return prev.id < curr.id ? prev : curr
							}).id

							this.loadTaskList(loading, oldest_task)
						}

						if (this.isLoading) {
							this.isLoading = false
						}

						if (!data.hasmore) {
							this.store.updateTaskStats()
						}
					})
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(error)
					this.isLoading = false
				})
		},

		loadCompletedTaskList(loading, oldest) {
			this.isLoading = loading

			let post_data = {
				organization_id: this.store.auth.user.organizations[0].id,
				completed: true,
			}

			if (oldest) {
				post_data.latest = oldest
			}

			post('/api/v1/task.list', post_data)
				.then(({ data }) => {
					if (oldest) {
						this.store.tasks.completed = this.store.tasks.completed.concat(
							data.tasks
						)
					} else {
						this.store.tasks.completed = data.tasks
					}

					this.$nextTick(function () {
						if (data.hasmore) {
							let oldest_task = data.tasks.reduce(function (prev, curr) {
								return prev.id < curr.id ? prev : curr
							}).id

							this.loadCompletedTaskList(loading, oldest_task)
						}

						if (this.isLoading) {
							this.showCompleted = true
							this.isLoading = false
						}
					})
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(error)
					this.isLoading = false
				})
		},

		newTask() {
			this.taskData = {}
			this.taskData.taskmedia = []
			this.taskData.user = this.store.auth.user
			this.taskData.parent = 'Tasks.vue'
			this.showTaskDetail = true
		},

		editTask(task) {
			this.taskData = task
			if (!task.taskmedia) {
				this.taskData.taskmedia = []
			}
			this.taskData.assignedTitle = this.getAssignee(task)
			this.taskData.parent = 'Tasks.vue'
			this.showTaskDetail = true
		},

		deleteTask(data) {
			if (data.completed) {
				this.store.tasks.completed = this.store.tasks.completed.filter(
					(task) => task.id !== data.task_id
				)
			} else {
				this.store.tasks.open = this.store.tasks.open.filter(
					(task) => task.id !== data.task_id
				)
			}

			this.store.updateTaskStats()
		},

		completeTask(task) {
			this.isLoading = true
			this.deleteTask({ task_id: task.id, completed: false })

			post('/api/v1/task.complete', { task_id: task.id })
				.then(({ data }) => {
					this.store.updateTaskStats()
					//this.loadCompletedTaskList(false);
					this.isLoading = false
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(error)
					this.isLoading = false
					this.store.tasks.open.push(task)
				})
		},

		unCompleteTask(task) {
			this.isLoading = true
			this.deleteTask({ task_id: task.id, completed: true })

			post('/api/v1/task.uncomplete', { task_id: task.id })
				.then(({ data }) => {
					this.store.tasks.open.push(task)
					this.store.updateTaskStats()
					this.isLoading = false
				})
				.catch((error) => {
					console.error(error)
					this.$alert.error(error)
					this.isLoading = false
					this.store.tasks.open.push(task)
					this.isLoading = false
				})
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';
.announcements-banner-tasks-open {
	top: 130px;
	transition-duration: 400ms;
	transition-delay: 1200ms;
	transition-timing-function: ease-in-out;
	transition-property: all;
}

.announcements-banner-tasks-close {
	top: 0;
}
#taskLink,
.hide-completed {
	color: var(--teal-300);
}
</style>
<style lang="scss">
@import 'src/sass/variables';
@include overrideScheme('dark') {
	.announcements-banner-tasks-close {
		background-color: var(--neutral-100);
		.task-content {
			.lg-heading,
			.task-list-header,
			.task-list-sub-header,
			.task-li-main,
			.task-li-sub {
				color: var(--neutral-800) !important;
			}
			.task-list li {
				background-color: var(--neutral-100) !important;
				border: solid 1px var(--neutral-200);
			}
		}
		#taskLink {
			background-color: var(--neutral-100) !important;
		}
	}
}
</style>
